import classNames from "classnames";
import { useEffect } from "react";
import { useLoadRecipientsDeprecated } from "react-migration/domains/sites/hooks";
import {
  LetterEventName,
  logLetterEvent,
} from "react-migration/domains/sites/letters/helpers/logLetterEvent";
import { Recipient } from "react-migration/domains/sites/typings";
import { Stores, useGlobalState, useTranslation } from "react-migration/lib";
import { updateSelectedRecipientIds } from "src/js/stores/letter/actions";
import { Feature } from "src/js/stores/user";
import { hasFeature } from "src/js/stores/user/actions";

import { Column, Table, withSkeletonLoading } from "@landtechnologies/components";

import { DeprecatedRecipientCell, TableTitle } from "./components";

export const SelectRecipientsTable = withSkeletonLoading(
  ({ hideSkeletonLoading }) => {
    const { t } = useTranslation();
    const usAccess = hasFeature(Feature.usAccess);
    const [{ siteCardId: siteId }] = useGlobalState(Stores.site);
    const [{ selectedRecipientIds, recipients, selectedStageId }] = useGlobalState(Stores.letter);

    const { loading } = useLoadRecipientsDeprecated({
      siteIds: siteId ? [siteId] : undefined,
      stageIds: selectedStageId ? [selectedStageId] : undefined,
    });

    useEffect(() => {
      if (!loading) hideSkeletonLoading();
    }, [loading, hideSkeletonLoading]);

    const columns: Column<Recipient>[] = [
      {
        label: t("sites.letters.name"),
        grow: true,
        render: (recipient: Recipient) => (
          <DeprecatedRecipientCell recipient={recipient} colKey={"fullName"} />
        ),
      },
      { label: t("sites.letters.address_line_1"), rowKey: "addressLine1", grow: true },
      { label: t("sites.letters.city"), rowKey: "city", grow: true },
      { label: t("sites.letters.region"), rowKey: "region", grow: true },
      { label: t("sites.letters.post_code"), rowKey: "postCode", grow: true },
      {
        label: t("sites.letters.site_name"),
        rowKey: "siteTitle",
        maxWidth: "400px",
      },
    ];

    if (!usAccess) {
      columns.splice(5, 0, { label: t("sites.letters.title"), rowKey: "title" });
    }

    const isSelectionDisabled = (recipient?: Recipient) =>
      (recipient?.hasOwnProperty("isGranted") && !recipient.isGranted) ?? false;

    const invalidRowClassName = (recipient?: Recipient) =>
      recipient?.hasOwnProperty("isValid") && !recipient?.isValid && !isSelectionDisabled(recipient)
        ? "atlas-bg-background-negative/10 hover:!atlas-bg-background-negative/20"
        : "";

    return (
      <div data-testid="select-recipients-table-container" className="atlas-h-full">
        <div
          className={classNames(
            "atlas-bg-background-light",
            "atlas-p-6",
            "atlas-h-full",
            "atlas-w-full",
            "atlas-grid",
            "atlas-overflow-x-auto"
          )}
        >
          <Table
            titleHeader={
              <TableTitle
                loading={loading}
                recipients={recipients}
                selectedRecipientIds={selectedRecipientIds}
                selectedStageId={selectedStageId}
              />
            }
            primaryKey={"_id"}
            rows={recipients ?? []}
            columns={columns}
            selection={{
              selectedRowIds: selectedRecipientIds ?? [],
              updateSelectedRowIds: updateSelectedRecipientIds,
              selectWithRowClick: true,
              onSelectAllRows: () => {
                logLetterEvent({
                  name: LetterEventName.LETTERS_FORM_SELECT_RECIPIENTS_STEP_ALL_RECIPIENTS_TOGGLED,
                });
              },
              onSelectRow: () => {
                logLetterEvent({
                  name: LetterEventName.LETTERS_FORM_SELECT_RECIPIENTS_STEP_RECIPIENT_TOGGLED,
                });
              },
              disabled: isSelectionDisabled,
            }}
            rowClassName={invalidRowClassName}
          />
        </div>
      </div>
    );
  },
  {
    showPadding: false,
  }
);
